/**
 * Messages route — the unified "Viestit" surface (030 US1; 050 US2).
 *
 * A SINGLE conversation list (owner decision 2026-09-17, recorded as the D-220
 * collapse amendment (i) in `docs/governance/decisions/`): the member's real
 * asianhallinta conversations read LIVE from the back office via
 * `member.cases.list` (route-on-read), rendered by the shared
 * `member-cases/CasesList` (subject + back-office handling-state pill +
 * per-row unread indication — D-229(b), D-230 — newest-first, 039 cursor
 * pagination). The former demo-fixture inbox is retired (D-081 / FR-001) and —
 * now that compose creates a REAL case (050 US1) — the member's own sent
 * messages appear here too, so the proto's separate "Lähetetyt" tab and its
 * device-local sent-record echo (030 deviation A) are RETIRED (050 US2): the
 * real conversation model has no sent/received split, and keeping a browser-
 * local copy would be a shadow copy (P1/P7). No fabricated rows survive.
 *
 * The nav unread badge (050 US3) and the handler-closed state (050 US5) are
 * wired separately. Detail lives at `/messages/:id`.
 */

import type { JSX } from 'react';
import { Suspense } from 'react';

import noMessagesImage from '../assets/no-messages.png';
import { useTranslate } from '../i18n/useTranslate.js';
import { CasesList, CasesLoading, CasesUnavailable } from '../member-cases/CasesList.js';
import { useComposeWizard } from '../member-messaging/compose/wizard/ComposeWizardProvider.js';
import { PlusIcon } from '../shell/icons.js';
import { PortErrorBoundary } from '../shell/PortErrorBoundary.js';

const pageClasses = 'flex flex-col gap-4';
const headingClasses = 'm-0 text-2xl font-bold text-foreground';
const emptyStateContentClasses = 'flex flex-col items-center gap-2 pt-24 text-center';
const illustrationClasses = 'h-40 w-40 object-contain';
const emptyCopyClasses = 'm-0 text-base text-muted-foreground';

// Floating CTA — pill anchored to the viewport bottom-right, mirroring the
// prototype's measured position. Opens the guided compose wizard.
const fabClasses =
  'fixed bottom-24 right-5 z-30 inline-flex h-12 items-center gap-2 rounded-full ' +
  'bg-brand-deep px-5 py-2 text-sm font-medium text-brand-deep-foreground shadow-lg ' +
  'no-underline transition active:scale-95 hover:bg-brand-deep/90 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

function EmptyPanel({ copy }: { readonly copy: string }): JSX.Element {
  return (
    <div className={emptyStateContentClasses}>
      <img src={noMessagesImage} alt="" className={illustrationClasses} />
      <p className={emptyCopyClasses}>{copy}</p>
    </div>
  );
}

export function Messages(): JSX.Element {
  const t = useTranslate();
  const emptyCopy = t('messages.empty');
  // Compose entry (027 US6, D-120 / FR-023): the FAB opens the guided wizard.
  const wizard = useComposeWizard();

  return (
    <section className={pageClasses} aria-labelledby="messages-title">
      <h2 id="messages-title" className={headingClasses}>
        {t('messages.title')}
      </h2>

      {/* The REAL conversations (route-on-read). The list suspends on the
          first-page read and refuses-not-fakes on failure. */}
      <PortErrorBoundary portName="memberCases" fallback={<CasesUnavailable />}>
        <Suspense fallback={<CasesLoading />}>
          <CasesList detailBase="/messages" emptyState={<EmptyPanel copy={emptyCopy} />} />
        </Suspense>
      </PortErrorBoundary>

      {/* fab-entry idiom (FR-023): opens the single contact experience. */}
      <button type="button" className={fabClasses} onClick={() => wizard.open()}>
        <PlusIcon aria-hidden="true" className="h-5 w-5" />
        {t('compose.wizard.entry.fab')}
      </button>
    </section>
  );
}
